import React, {
	useEffect,
	useState,
	useMemo,
	useRef,
	useImperativeHandle,
	forwardRef,
} from "react";
import { Button } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { ArrowSquareUp, ArrowSquareDown, FileXls } from "@phosphor-icons/react";
import Select from "react-select";

const MultipleWithNoButtons = forwardRef(({
	goal,
	currentYear,
	onYearChange,
	onExportXLSX,
	availableYears = []
}, ref) => {
	const navigate = useNavigate();
	const [lineChartData, setLineChartData] = useState([]);
	const [loading, setLoading] = useState(true);

	// ranges independentes para os dois eixos
	const [maxValueLeft, setMaxValueLeft] = useState(undefined);
	const [minValueLeft, setMinValueLeft] = useState(undefined);
	const [maxValueRight, setMaxValueRight] = useState(undefined);
	const [minValueRight, setMinValueRight] = useState(undefined);

	const chartRef = useRef(null);

	const buildPeriods = (periodicity) => {
		const p = (periodicity || "").toLowerCase();
		switch (p) {
			case "bimestral":
				return [
					{ id: 1, label: "Jan-Fev", monthStart: 1 },
					{ id: 2, label: "Mar-Abr", monthStart: 3 },
					{ id: 3, label: "Mai-Jun", monthStart: 5 },
					{ id: 4, label: "Jul-Ago", monthStart: 7 },
					{ id: 5, label: "Set-Out", monthStart: 9 },
					{ id: 6, label: "Nov-Dez", monthStart: 11 },
				];
			case "trimestral":
				return [
					{ id: 1, label: "Jan-Mar", monthStart: 1 },
					{ id: 2, label: "Abr-Jun", monthStart: 4 },
					{ id: 3, label: "Jul-Set", monthStart: 7 },
					{ id: 4, label: "Out-Dez", monthStart: 10 },
				];
			case "quadrimestral":
				return [
					{ id: 1, label: "Jan-Abr", monthStart: 1 },
					{ id: 2, label: "Mai-Ago", monthStart: 5 },
					{ id: 3, label: "Set-Dez", monthStart: 9 },
				];
			case "semestral":
				return [
					{ id: 1, label: "Jan-Jun", monthStart: 1 },
					{ id: 2, label: "Jul-Dez", monthStart: 7 },
				];
			case "anual":
				return [{ id: 1, label: "Jan-Dez", monthStart: 1 }];
			case "mensal":
			default:
				return [
					{ id: 1, label: "Jan", monthStart: 1 },
					{ id: 2, label: "Fev", monthStart: 2 },
					{ id: 3, label: "Mar", monthStart: 3 },
					{ id: 4, label: "Abr", monthStart: 4 },
					{ id: 5, label: "Mai", monthStart: 5 },
					{ id: 6, label: "Jun", monthStart: 6 },
					{ id: 7, label: "Jul", monthStart: 7 },
					{ id: 8, label: "Ago", monthStart: 8 },
					{ id: 9, label: "Set", monthStart: 9 },
					{ id: 10, label: "Out", monthStart: 10 },
					{ id: 11, label: "Nov", monthStart: 11 },
					{ id: 12, label: "Dez", monthStart: 12 },
				];
		}
	};

	const periods = useMemo(
		() => buildPeriods(goal?.periodicity),
		[goal?.periodicity],
	);

	const metaConfig = useMemo(() => {
		return goal?.dados?.find((item) => item.goals_config?.type === "0")
			?.goals_config;
	}, [goal?.dados]);

	useImperativeHandle(ref, () => ({
		getChart: () => chartRef.current,
	}));

	useEffect(() => {
		setLoading(true);

		const dadosOrganizados = goal.dados.reduce((agrupados, item) => {
			if (!agrupados[item.goals_config_id]) {
				agrupados[item.goals_config_id] = [];
			}
			agrupados[item.goals_config_id].push(item);
			return agrupados;
		}, {});

		Object.keys(dadosOrganizados).forEach((goalsConfigId) => {
			dadosOrganizados[goalsConfigId].sort(
				(a, b) => parseInt(a.month) - parseInt(b.month),
			);
		});

		const values = Object.entries(dadosOrganizados)
			// Ordenar por sequence antes de criar as séries (sequence vem como string)
			.sort(([goalsConfigIdA, itemsA], [goalsConfigIdB, itemsB]) => {
				const sequenceA = parseInt(itemsA[0]?.goals_config?.sequence || 0);
				const sequenceB = parseInt(itemsB[0]?.goals_config?.sequence || 0);
				return sequenceA - sequenceB;
			})
			.map(([goalsConfigId, items]) => {
				const firstRecord = items[0];

				if (
					firstRecord.goals_config?.display_on_chart !== 1 &&
					firstRecord.goals_config?.display_on_chart !== 2
				) {
					return null;
				}

				const isSecondAxis =
					firstRecord.goals_config.show_on_second_axis === "1";

				const serie = {
					type:
						firstRecord.goals_config.display_on_chart === 2
							? "line"
							: "column",
					name: firstRecord.goals_config.name,
					data: periods.map((period) => {
						const found = items.find(
							(field) => parseInt(field.month) === period.monthStart,
						);
						return found ? parseFloat(found.value) : null;
					}),
					// Marca se usa eixo secundário (será usado na configuração do yaxis)
					useSecondAxis: isSecondAxis,
					decimals:
						firstRecord.goals_config?.decimals_cases != null
							? Number(firstRecord.goals_config.decimals_cases)
							: 2,
				};

				if (firstRecord.goals_config.color) {
					serie.color = firstRecord.goals_config.color;
				}

				return serie;
			})
			.filter(Boolean);

		// Calcular min/max de cada eixo separadamente
		// Extrair todos os valores numéricos das séries visíveis
		if (values.length > 0) {
			// Eixo esquerdo (principal)
			const leftSeriesValues = values
				.filter((s) => !s.useSecondAxis)
				.flatMap((s) => s.data)
				.filter(val => val !== null && val !== undefined && !isNaN(val));

			if (leftSeriesValues.length > 0) {
				const dataMin = Math.min(...leftSeriesValues);
				const dataMax = Math.max(...leftSeriesValues);
				
				if (dataMin >= 0) {
					// Todos positivos: garantir que zero apareça
					setMaxValueLeft(dataMax * 1.1);
					setMinValueLeft(0);
				} else if (dataMax <= 0) {
					// Todos negativos: garantir que zero apareça
					setMaxValueLeft(0);
					setMinValueLeft(dataMin * 1.1);
				} else {
					// Valores mistos: deixar ApexCharts calcular automaticamente (como no exemplo)
					setMaxValueLeft(undefined);
					setMinValueLeft(undefined);
				}
			}

			// Eixo direito (secundário)
			const rightSeriesValues = values
				.filter((s) => s.useSecondAxis)
				.flatMap((s) => s.data)
				.filter(val => val !== null && val !== undefined && !isNaN(val));

			if (rightSeriesValues.length > 0) {
				const dataMin = Math.min(...rightSeriesValues);
				const dataMax = Math.max(...rightSeriesValues);
				
				if (dataMin >= 0) {
					// Todos positivos: garantir que zero apareça
					setMaxValueRight(dataMax * 1.1);
					setMinValueRight(0);
				} else if (dataMax <= 0) {
					// Todos negativos: garantir que zero apareça
					setMaxValueRight(0);
					setMinValueRight(dataMin * 1.1);
				} else {
					// Valores mistos: deixar ApexCharts calcular automaticamente (como no exemplo)
					setMaxValueRight(undefined);
					setMinValueRight(undefined);
				}
			}
		}

		setLineChartData(values);
		setLoading(false);
	}, [goal]);

	const options = useMemo(
		() => ({
			chart: {
				events: {
					mounted(chartContext) {
						chartRef.current = chartContext;
					},
					dataPointSelection: () => false,
					legendClick: () => false,
					markerClick: () => false,
				},
				height: "100%",
				toolbar: { show: false },
				parentHeightOffset: 0,
				redrawOnParentResize: true,
				redrawOnWindowResize: true,
				selection: { enabled: false },
				zoom: { enabled: false },
				ignoreEmptySeries: false, // Não ignora séries com valores null
			},
			stroke: { curve: "straight", width: 2 },
			xaxis: {
				categories: periods.map((item) => item.label),
				title: { text: "Período" },
				crosshairs: {
					show: true,
					position: "back",
					stroke: { width: 1, dashArray: 4, color: "#999" },
				},
				tooltip: { enabled: false },
			},
			yaxis: (() => {
				// Cria apenas 2 eixos: principal e secundário
				const yaxisConfig = [];

				// Eixo principal (esquerda) - para séries sem useSecondAxis
				const leftSeries = lineChartData.filter(serie => !serie.useSecondAxis);
				if (leftSeries.length > 0) {
					const yaxisConfigLeft = {
						seriesName: leftSeries.map(s => s.name), // Array com nomes das séries
						title: { text: "" },
						labels: {
							formatter: (value) => {
								if (value === null || value === undefined || isNaN(value)) {
									return "0,00";
								}
								// Arredonda valores muito próximos de zero para zero (evita -0,00)
								const normalizedValue = Math.abs(value) < 0.01 ? 0 : value;
								return normalizedValue.toLocaleString("pt-BR", {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								});
							},
						},
					};
					// Só define min/max se não forem undefined (valores mistos deixam undefined para ApexCharts calcular)
					if (minValueLeft !== undefined && maxValueLeft !== undefined) {
						yaxisConfigLeft.min = minValueLeft;
						yaxisConfigLeft.max = maxValueLeft;
					}
					yaxisConfig.push(yaxisConfigLeft);
				}

				// Eixo secundário (direita) - para séries com useSecondAxis
				const rightSeries = lineChartData.filter(serie => serie.useSecondAxis);
				if (rightSeries.length > 0) {
					const yaxisConfigRight = {
						seriesName: rightSeries.map(s => s.name), // Array com nomes das séries
						opposite: true,
						title: { text: "" },
						labels: {
							formatter: (value) => {
								if (value === null || value === undefined || isNaN(value)) {
									return "0,00";
								}
								// Arredonda valores muito próximos de zero para zero (evita -0,00)
								const normalizedValue = Math.abs(value) < 0.01 ? 0 : value;
								return (
									normalizedValue.toLocaleString("pt-BR", {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})
								);
							},
						},
					};
					// Só define min/max se não forem undefined (valores mistos deixam undefined para ApexCharts calcular)
					if (minValueRight !== undefined && maxValueRight !== undefined) {
						yaxisConfigRight.min = minValueRight;
						yaxisConfigRight.max = maxValueRight;
					}
					yaxisConfig.push(yaxisConfigRight);
				}

				return yaxisConfig;
			})(),
			legend: { position: "bottom", horizontalAlign: "center" },
			plotOptions: {
				bar: { columnWidth: "80%", dataLabels: { position: "top" } },
			},
			tooltip: {
				enabled: true,
				intersect: false,
				shared: true,
				followCursor: true,
				custom: function ({ series, seriesIndex, dataPointIndex, w }) {
					const monthName = w.globals.categoryLabels[dataPointIndex];
					let tooltipContent = `<div style="padding: 10px; background: white; border-radius: 5px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">`;
					tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; color: #333;">${monthName}</div>`;

					series.forEach((serieData, index) => {
						if (!serieData || !w.globals.seriesNames[index]) return;
						const serieName = w.globals.seriesNames[index];
						const value = serieData[dataPointIndex];
						if (value !== null && value !== undefined && !isNaN(value)) {
							const decimals = Number(
								lineChartData[index]?.decimals ?? 2,
							);
							const formattedValue = value.toLocaleString("pt-BR", {
								minimumFractionDigits: decimals,
								maximumFractionDigits: decimals,
							});
							const color = w.globals.colors[index];
							tooltipContent += `<div style="display: flex; align-items: center; margin-bottom: 4px;">`;
							tooltipContent += `<div style="width: 12px; height: 12px; background: ${color}; border-radius: 50%; margin-right: 8px;"></div>`;
							tooltipContent += `<span style="color: #333; font-size: 13px;">${serieName}: <strong>${formattedValue}</strong></span>`;
							tooltipContent += `</div>`;
						}
					});

					tooltipContent += `</div>`;
					return tooltipContent;
				},
			},
			markers: { size: 5, hover: { size: 7 } },
			responsive: [
				{ breakpoint: 480, options: { legend: { fontSize: "8px" } } },
			],
		}),
		[minValueLeft, maxValueLeft, minValueRight, maxValueRight, lineChartData],
	);

	const editarItem = () => {
		navigate(`form/${goal.id}`);
	};

	return (
		<div style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
			{/* topo */}
			<div style={{
				flexShrink: 0,
				marginBottom: "10px",
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				flexWrap: "wrap",
				gap: "10px"
			}}>
				<div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
					<h4 style={{ fontSize: "1.2rem", fontWeight: "600", margin: 0 }}>
						{goal?.intention_goals}
					</h4>
					{metaConfig?.type_meta_definition && (
						<div style={{
							display: "flex",
							alignItems: "center",
							gap: "5px",
							padding: "6px 10px",
							borderRadius: "4px",
							backgroundColor: metaConfig.type_meta_definition === "maior_melhor" ? "#e3f2fd" : "#ffebee",
							border: `1px solid ${metaConfig.type_meta_definition === "maior_melhor" ? "#0d6efd" : "#dc3545"}`
						}}>
							{metaConfig.type_meta_definition === "maior_melhor" ? (
								<ArrowSquareUp size={18} color="#0d6efd" />
							) : (
								<ArrowSquareDown size={18} color="#dc3545" />
							)}
							<span style={{
								fontSize: "14px",
								color: metaConfig.type_meta_definition === "maior_melhor" ? "#0d6efd" : "#dc3545",
								fontWeight: "400"
							}}>
								{metaConfig.type_meta_definition === "maior_melhor" ? "Maior Melhor" : "Menor Melhor"}
							</span>
						</div>
					)}
				</div>

				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					{availableYears.length > 0 && onYearChange && (
						<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
							<div style={{ width: "140px", position: "relative", zIndex: 500 }}>
								<Select
									value={{
										value: currentYear || new Date().getFullYear(),
										label: (currentYear || new Date().getFullYear()).toString()
									}}
									onChange={(selectedOption) => onYearChange(selectedOption.value)}
									options={availableYears.map(year => ({
										value: year,
										label: year.toString()
									}))}
									isSearchable={false}
									menuPortalTarget={document.body}
									menuPosition="fixed"
									styles={{
										control: (base) => ({
											...base,
											minHeight: '36px',
											height: '36px',
											fontSize: '16px',
											border: '1px solid #ced4da',
											borderRadius: '4px'
										}),
										valueContainer: (base) => ({
											...base,
											height: '34px',
											padding: '0 8px'
										}),
										input: (base) => ({
											...base,
											margin: '0px'
										}),
										indicatorSeparator: () => ({ display: 'none' }),
										indicatorsContainer: (base) => ({ ...base, height: '34px' }),
										menu: (base) => ({
											...base,
											backgroundColor: 'white',
											boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
											border: '1px solid #e5e7eb',
											borderRadius: '4px'
										}),
										menuList: (base) => ({ ...base, backgroundColor: 'white', padding: '4px 0' }),
										option: (base, state) => ({
											...base,
											backgroundColor: state.isFocused ? '#f3f4f6' : 'white',
											color: '#374151',
											cursor: 'pointer',
											fontSize: '16px',
											padding: '8px 12px'
										}),
										menuPortal: (base) => ({ ...base, zIndex: 9999 })
									}}
								/>
							</div>
						</div>
					)}

					{onExportXLSX && (
						<Button
							onClick={() => onExportXLSX()}
							style={{
								background: "none",
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 2,
							}}
						>
							<FileXls size={16} />
							Exportar XLSX
						</Button>
					)}
				</div>
			</div>

			<div style={{ flex: 1, width: "100%", height: "100%", position: "relative" }}>
				{loading ? (
					<div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
						<span className="spinner-border spinner-border-sm" />
					</div>
				) : (
					<div style={{ height: "100%" }}>
						<ReactApexChart
							series={lineChartData}
							options={options}
							height="100%"
							width="100%"
							className="apex-charts"
						/>
					</div>
				)}
			</div>
		</div>
	);
});

MultipleWithNoButtons.displayName = "MultipleWithNoButtons";
export default MultipleWithNoButtons;
